// Ops settings: plain-English fields, editing as strings, validation back to wire values.
// Defaults and units follow platform_settings in migration 0003 (money in centavos, km, minutes).
import { peso } from './format.js';

/** @typedef {'delivery' | 'express' | 'cod' | 'dispatch' | 'grill' | 'mart' | 'handoff' | 'marketplace' | 'riders'} SettingKey */
/**
 * @typedef {{
 *   name: string;
 *   kind: 'peso' | 'km' | 'minutes' | 'seconds' | 'days' | 'count' | 'bool' | 'percent';
 *   label: string;
 *   help?: string;
 *   min?: number;
 *   max?: number;
 *   step?: number;
 *   default?: number;
 *   defaultOn?: boolean;
 *   optional?: boolean;
 * }} FieldSpec
 * `optional`: a field newer than some servers. A draft that doesn't carry it at all (undefined)
 * leaves it to the server instead of failing; drafts from toDraft() always carry it (default shown).
 * `percent`: basis points on the wire (1500), a percent in the input ("15"). `defaultOn`: what a
 * switch shows when the server doesn't send the value yet.
 */

/** @type {{ key: SettingKey; title: string; intro: string; fields: FieldSpec[] }[]} */
export const SETTINGS_SPEC = [
  {
    key: 'delivery',
    title: 'Delivery fees and distance',
    intro: 'What customers pay to get their order delivered, and how far we go.',
    fields: [
      { name: 'baseFeeCentavos', kind: 'peso', label: 'Delivery fee starts at', min: 0, max: 500 },
      { name: 'includedKm', kind: 'km', label: 'Distance included in that fee', min: 0, max: 10, step: 0.1 },
      { name: 'perKmCentavos', kind: 'peso', label: 'Each extra km adds', min: 0, max: 200 },
      { name: 'maxKm', kind: 'km', label: 'We deliver up to', min: 1, max: 30, step: 0.1 },
      {
        name: 'clusterRadiusKm',
        kind: 'km',
        label: 'Shops this close share one rider',
        help: 'Shops within this distance of the route are picked up by the same rider.',
        min: 1,
        max: 2,
        step: 0.1,
      },
      { name: 'extraStopCentavos', kind: 'peso', label: 'Each extra shop pickup adds', min: 0, max: 200 },
    ],
  },
  {
    key: 'express',
    title: 'Express',
    intro: 'The faster tier: priority prep, a dedicated rider and a time promise.',
    fields: [
      { name: 'enabled', kind: 'bool', label: 'Offer Express' },
      { name: 'surchargeCentavos', kind: 'peso', label: 'Express costs extra', min: 0, max: 500 },
      { name: 'promiseMinutes', kind: 'minutes', label: 'Express arrives within', min: 10, max: 120 },
      { name: 'lateCreditCentavos', kind: 'peso', label: 'If Express is late, the customer gets back', min: 0, max: 500 },
      { name: 'maxActive', kind: 'count', label: 'Express orders on the road at once, at most', min: 1, max: 100 },
      {
        name: 'escalateAfterSeconds',
        kind: 'seconds',
        label: 'Flag an Express run with no rider after',
        help: 'Counted from when the order was placed. Ops gets an alert and the run shows “Escalated” on the Express monitor.',
        min: 30,
        max: 900,
        default: 120,
        optional: true,
      },
      {
        name: 'riskMarginMinutes',
        kind: 'minutes',
        label: 'Call a run “at risk” when it has less spare time than',
        help: 'Compares the rider’s ETA with the promise. The customer gets one honest heads-up the first time.',
        min: 0,
        max: 15,
        default: 3,
        optional: true,
      },
      {
        name: 'creditExpiryDays',
        kind: 'days',
        label: 'Late and goodwill credits last',
        help: 'Credits pay the delivery fee and Express surcharge of a later order. Unused credit expires after this.',
        min: 1,
        max: 180,
        default: 30,
        optional: true,
      },
    ],
  },
  {
    key: 'cod',
    title: 'Cash on delivery',
    intro: 'How much cash riders carry.',
    fields: [
      { name: 'riderChangeLimitCentavos', kind: 'peso', label: 'Change a rider carries, at most', min: 0, max: 5000 },
      {
        name: 'riderCashLimitCentavos',
        kind: 'peso',
        label: 'Rider remits cash above',
        help: 'Ops gets an alert when a rider holds more than this.',
        min: 500,
        max: 50000,
      },
    ],
  },
  {
    key: 'dispatch',
    title: 'Dispatch',
    intro: 'When riders get booked.',
    fields: [
      {
        name: 'assignBeforeReadyMinutes',
        kind: 'minutes',
        label: 'Book the rider this long before an order is ready',
        help: 'So food never waits for a rider.',
        min: 0,
        max: 30,
      },
    ],
  },
  {
    key: 'grill',
    title: 'Grill kitchen',
    intro: 'Made-to-order food: when the rider gets booked.',
    fields: [
      {
        name: 'preAssignMinutes',
        kind: 'minutes',
        label: 'Rider pre-assign',
        help: 'Assign a rider this many minutes before food is ready.',
        min: 0,
        max: 30,
        default: 5,
      },
    ],
  },
  {
    key: 'mart',
    title: 'Grocery',
    intro: 'Sari-sari staples: when customers see “Only 3 left”.',
    fields: [
      {
        name: 'lowStockThreshold',
        kind: 'count',
        label: 'Show “Only a few left” at',
        help: 'Cards and the cart say “Only 3 left” when this many or fewer are in stock.',
        min: 1,
        max: 100,
        default: 5,
      },
    ],
  },
  {
    key: 'handoff',
    title: 'Ready for rider',
    intro: 'The handoff board in each shop: when an old order steps aside so it doesn’t crowd the live ones.',
    fields: [
      {
        name: 'staleAfterMinutes',
        kind: 'minutes',
        label: 'Move to Stuck after (minutes)',
        help: 'Orders older than this go to a folded “Stuck” group at the bottom of the board, with their age. Nothing is dropped.',
        min: 30,
        max: 1440,
        default: 240,
        optional: true,
      },
    ],
  },
  {
    key: 'marketplace',
    title: 'Partner shops',
    intro: 'What new partner shops pay PopCart. Each shop’s own rate is on its page under Shops.',
    fields: [
      {
        name: 'defaultCommissionBps',
        kind: 'percent',
        label: 'Default commission',
        help: 'A share of item sales. New shops start with this; changing it doesn’t touch existing shops.',
        min: 0,
        max: 50,
        default: 1500,
        optional: true,
      },
    ],
  },
  {
    key: 'riders',
    title: 'Riders',
    intro: 'Who can go online in the rider app.',
    fields: [
      {
        name: 'requireCompleteProfile',
        kind: 'bool',
        label: 'Riders need a complete profile to go online',
        help: 'Photo, vehicle, plate and an emergency contact. Riders see what’s missing when they try to go online.',
        defaultOn: true,
      },
    ],
  },
];

/** @param {SettingKey} key */
export function specFor(key) {
  const s = SETTINGS_SPEC.find((x) => x.key === key);
  if (!s) throw new Error(`unknown setting ${key}`);
  return s;
}

/**
 * Wire value → what the input shows (pesos for centavo fields).
 * @param {FieldSpec} f
 * @param {unknown} v
 * @returns {string | boolean}
 */
export function toInput(f, v) {
  if (f.kind === 'bool') return v === undefined || v === null ? f.defaultOn === true : v === true;
  if ((v === undefined || v === null) && typeof f.default === 'number') v = f.default;
  if (typeof v !== 'number' || !Number.isFinite(v)) return '';
  if (f.kind === 'peso' || f.kind === 'percent') return String(Math.round(v) / 100);
  return String(v);
}

/**
 * Make an editable draft from a server settings object.
 * @param {SettingKey} key
 * @param {Record<string, unknown> | null | undefined} value
 * @returns {Record<string, string | boolean>}
 */
export function toDraft(key, value) {
  /** @type {Record<string, string | boolean>} */
  const d = {};
  for (const f of specFor(key).fields) d[f.name] = toInput(f, value ? value[f.name] : undefined);
  return d;
}

/** "1.5 km", "3 min", "120 s", "1 day", "30 days". @param {FieldSpec} f @param {number} x */
function withUnit(f, x) {
  if (f.kind === 'days') return `${x} ${x === 1 ? 'day' : 'days'}`;
  if (f.kind === 'percent') return `${x}%`;
  return `${x}${f.kind === 'km' ? ' km' : f.kind === 'minutes' ? ' min' : f.kind === 'seconds' ? ' s' : ''}`;
}

/**
 * Validate a draft. Returns the full wire object (unknown server keys preserved) or field errors.
 * @param {SettingKey} key
 * @param {Record<string, string | boolean>} draft
 * @param {Record<string, unknown>} [base] the current server value, to keep keys we don't edit
 * @returns {{ ok: true; value: Record<string, unknown> } | { ok: false; errors: Record<string, string> }}
 */
export function validateSettings(key, draft, base = {}) {
  /** @type {Record<string, string>} */
  const errors = {};
  /** @type {Record<string, unknown>} */
  const value = { ...base };
  for (const f of specFor(key).fields) {
    const raw = draft[f.name];
    if (f.kind === 'bool') {
      value[f.name] = raw === true;
      continue;
    }
    if (raw === undefined && f.optional) continue; // newer field this draft doesn't carry: the server keeps it
    const s = typeof raw === 'string' ? raw.trim().replace(/,/g, '') : '';
    if (!s) {
      errors[f.name] = 'Enter a number.';
      continue;
    }
    if (!/^\d+(\.\d+)?$/.test(s)) {
      errors[f.name] = 'Use digits only, like 1.5';
      continue;
    }
    const n = Number(s);
    const lo = f.min ?? 0;
    const hi = f.max ?? Number.MAX_SAFE_INTEGER;
    const shown = (/** @type {number} */ x) => (f.kind === 'peso' ? peso(x * 100) : withUnit(f, x));
    if (n < lo || n > hi) {
      errors[f.name] = `Between ${shown(lo)} and ${shown(hi)}.`;
      continue;
    }
    if (f.kind === 'peso') {
      if (!/^\d+(\.\d{1,2})?$/.test(s)) {
        errors[f.name] = 'Pesos and centavos only, like 49.50';
        continue;
      }
      value[f.name] = Math.round(n * 100);
    } else if (f.kind === 'percent') {
      if (!/^\d+(\.\d{1,2})?$/.test(s)) {
        errors[f.name] = 'Two decimal places at most, like 12.5';
        continue;
      }
      value[f.name] = Math.round(n * 100);
    } else if (f.kind === 'km') {
      if (!/^\d+(\.\d)?$/.test(s)) {
        errors[f.name] = 'One decimal place at most, like 1.5';
        continue;
      }
      value[f.name] = Math.round(n * 10) / 10;
    } else {
      if (!Number.isInteger(n)) {
        errors[f.name] = 'Whole numbers only.';
        continue;
      }
      value[f.name] = n;
    }
  }
  // Cross-field rules.
  if (key === 'delivery' && !errors.includedKm && !errors.maxKm && Number(value.includedKm) > Number(value.maxKm)) {
    errors.includedKm = 'Can’t be more than the delivery limit.';
  }
  if (
    key === 'cod' &&
    !errors.riderChangeLimitCentavos &&
    !errors.riderCashLimitCentavos &&
    Number(value.riderChangeLimitCentavos) > Number(value.riderCashLimitCentavos)
  ) {
    errors.riderChangeLimitCentavos = 'Can’t be more than the remit limit.';
  }
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, value };
}

/**
 * Plain-English line for one field, e.g. "Shops this close share one rider: 1.5 km".
 * @param {FieldSpec} f
 * @param {unknown} v wire value
 */
export function describeField(f, v) {
  if (f.kind === 'bool') return `${f.label}: ${(v === undefined || v === null ? f.defaultOn === true : v === true) ? 'yes' : 'no'}`;
  if ((v === undefined || v === null) && typeof f.default === 'number') v = f.default;
  if (typeof v !== 'number' || !Number.isFinite(v)) return `${f.label}: not set`;
  if (f.kind === 'peso') return `${f.label}: ${peso(v)}`;
  if (f.kind === 'percent') return `${f.label}: ${withUnit(f, Math.round(v) / 100)}`;
  return `${f.label}: ${withUnit(f, v)}`;
}

/**
 * Has the draft changed from the server value?
 * @param {SettingKey} key
 * @param {Record<string, string | boolean>} draft
 * @param {Record<string, unknown> | null | undefined} value
 */
export function isDirty(key, draft, value) {
  const orig = toDraft(key, value);
  return specFor(key).fields.some((f) => {
    const a = draft[f.name];
    const b = orig[f.name];
    if (typeof a === 'string' && typeof b === 'string') return a.trim() !== b;
    return a !== b;
  });
}
